/**
 * Decode HTML entities (e.g. `&amp;` → `&`, `&#x27;` → `'`) using the
 * browser's parser. Safe for short text like case names and release titles —
 * don't pass entire DOJ release bodies through this and re-inject into HTML.
 */
export function decodeHtmlEntities(text: string | null | undefined): string {
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Shared rendering helpers for press release body text.
 *
 * DOJ release HTML lands in the analytics DB with raw entities (`&nbsp;`,
 * `&amp;`, curly quotes) and runs as one massive paragraph. `cleanBodyText`
 * does light HTML-entity decoding, normalizes smart quotes, and adds
 * paragraph breaks at sentence boundaries that typically separate sections
 * in a DOJ press release.
 *
 * Render the output with `whiteSpace: 'pre-wrap'` to honor the inserted
 * newlines. NEVER pass DOJ body text to `dangerouslySetInnerHTML`.
 */
export function cleanBodyText(text: string | null | undefined): string {
  if (!text) return '';

  // Replace HTML entities
  let cleaned = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Normalize curly/smart quotes to straight quotes
  cleaned = cleaned
    .replace(/[“”„‟″‶]/g, '"')
    .replace(/[‘’‚‛′‵]/g, "'");

  // Add paragraph breaks at common DOJ-release section boundaries
  cleaned = cleaned
    .replace(/\.\s+"/g, '.\n\n"')
    .replace(/\.\s+(The [A-Z])/g, '.\n\n$1')
    .replace(/\.\s+(According to)/g, '.\n\n$1')
    .replace(/\.\s+(Under the)/g, '.\n\n$1')
    .replace(/\.\s+(In addition)/g, '.\n\n$1')
    .replace(/\.\s+(As part of)/g, '.\n\n$1')
    .replace(/\.\s+(This (case|matter|settlement|investigation))/g, '.\n\n$1')
    .replace(/\.\s+(Today'?s)/g, '.\n\n$1');

  // Collapse multiple spaces left behind by entity replacement
  cleaned = cleaned.replace(/  +/g, ' ');

  return cleaned.trim();
}
